import { db } from '../firebase/config';
import { collection, addDoc, getDocs, query, where, deleteDoc } from 'firebase/firestore';

// Sample testimonial data
const testimonials = [
  {
    id: 1,
    name: 'Michael Johnson',
    role: 'Owner, Johnson Decking',
    image: 'https://randomuser.me/api/portraits/men/32.jpg',
    content: "Since implementing Nail the Cost, we've seen a 40% reduction in estimation errors and a 25% increase in our overall profit margins. The material calculation feature alone has saved us thousands in potential waste.",
    rating: 5,
    color: 'bg-blue-50'
  },
  {
    id: 2,
    name: 'Sarah Williams',
    role: 'Project Manager, Williams & Co',
    image: 'https://randomuser.me/api/portraits/women/44.jpg',
    content: "The scheduling features in Nail the Cost have transformed how we manage our crews. We can now account for weather delays and material delivery times automatically, which has drastically improved our project timelines.",
    rating: 5,
    color: 'bg-purple-50'
  },
  {
    id: 3,
    name: 'David Thompson',
    role: 'CEO, Thompson Deck Builders',
    image: 'https://randomuser.me/api/portraits/men/62.jpg',
    content: "What impressed me most about Nail the Cost is how quickly it paid for itself. Within just two projects, the precise quoting and budgeting tools had already saved us more than the annual subscription.",
    rating: 4,
    color: 'bg-emerald-50'
  },
  {
    id: 4,
    name: 'Jessica Parker',
    role: 'Operations Director, Parker Decking Solutions',
    image: 'https://randomuser.me/api/portraits/women/28.jpg',
    content: "The client portal is a game changer. Our customers love being able to track progress, approve designs, and communicate directly through the platform. It's drastically reduced our back-and-forth emails.",
    rating: 5,
    color: 'bg-amber-50'
  },
  {
    id: 5,
    name: 'Robert Garcia',
    role: 'Owner, Garcia Custom Decks',
    image: 'https://randomuser.me/api/portraits/men/77.jpg',
    content: "As someone who was initially skeptical about software solutions, I can confidently say Nail the Cost has made me a believer. The intuitive interface made it easy to get my entire team on board quickly.",
    rating: 5,
    color: 'bg-red-50'
  },
  {
    id: 6,
    name: 'Emily Chen',
    role: 'Financial Controller, Chen Outdoor Living',
    image: 'https://randomuser.me/api/portraits/women/63.jpg',
    content: "The reporting capabilities have given us unprecedented insights into our business operations. We can now identify which types of projects are most profitable and focus our marketing efforts accordingly.",
    rating: 4,
    color: 'bg-indigo-50'
  }
];

// Sample case studies data
const caseStudies = [
  {
    id: 1,
    company: 'Thompson Deck Builders',
    title: 'How Thompson Increased Profits by 35%',
    description: 'Learn how this medium-sized decking company transformed their operations with Nail the Cost.',
    image: '/case-study-1.jpg',
    color: 'from-blue-500 to-blue-700'
  },
  {
    id: 2,
    company: 'Parker Decking Solutions',
    title: 'Reducing Material Waste by 42%',
    description: 'See how precise calculations helped this company dramatically reduce waste and increase sustainability.',
    image: '/case-study-2.jpg',
    color: 'from-purple-500 to-purple-700'
  },
  {
    id: 3,
    company: 'Garcia Custom Decks',
    title: 'From 3 to 12 Projects Per Month',
    description: 'How streamlined operations and efficient scheduling helped this small business triple their capacity.',
    image: '/case-study-3.jpg',
    color: 'from-emerald-500 to-emerald-700'
  }
];

// Function to clear existing data from a collection
const clearCollection = async (collectionName) => {
  try {
    console.log(`Starting to clear collection: ${collectionName}`);
    const q = query(collection(db, collectionName));
    const querySnapshot = await getDocs(q);
    
    console.log(`Found ${querySnapshot.docs.length} documents to delete in ${collectionName}`);
    
    const deletePromises = [];
    querySnapshot.forEach((doc) => {
      console.log(`Preparing to delete document with ID: ${doc.id}`);
      deletePromises.push(deleteDoc(doc.ref));
    });
    
    await Promise.all(deletePromises);
    console.log(`Cleared collection: ${collectionName}`);
  } catch (error) {
    console.error(`Error clearing ${collectionName}:`, error);
    throw error; // Rethrow to handle in the calling function
  }
};

// Function to add documents to a collection
const addDocuments = async (collectionName, documents) => {
  try {
    console.log(`Starting to add documents to ${collectionName}`);
    const collectionRef = collection(db, collectionName);
    
    for (const document of documents) {
      console.log(`Adding document for ${collectionName} with data:`, document);
      const docRef = await addDoc(collectionRef, document);
      console.log(`Document added with ID: ${docRef.id}`);
    }
    
    console.log(`Added ${documents.length} documents to ${collectionName}`);
  } catch (error) {
    console.error(`Error adding documents to ${collectionName}:`, error);
    throw error; // Rethrow to handle in the calling function
  }
};

// Main function to populate Firestore
const populateFirestore = async () => {
  try {
    console.log('Starting Firestore population process...');
    console.log('Firestore database reference:', db);
    
    // Clear existing data
    await clearCollection('testimonials');
    await clearCollection('caseStudies');
    
    // Add new data
    await addDocuments('testimonials', testimonials);
    await addDocuments('caseStudies', caseStudies);
    
    console.log('Firestore population completed successfully');
    
    // Verify data was added
    const testimonialsSnapshot = await getDocs(collection(db, 'testimonials'));
    const caseStudiesSnapshot = await getDocs(collection(db, 'caseStudies'));
    
    console.log(`Verification - Testimonials count: ${testimonialsSnapshot.docs.length}`);
    console.log(`Verification - Case Studies count: ${caseStudiesSnapshot.docs.length}`);
    
    return {
      testimonialsCount: testimonialsSnapshot.docs.length,
      caseStudiesCount: caseStudiesSnapshot.docs.length
    };
  } catch (error) {
    console.error('Error populating Firestore:', error);
    throw error;
  }
};

// Export the function to be called from elsewhere
export default populateFirestore;

// For development testing
// populateFirestore(); 